<template>
    <div class="favorites-page">
        <breadcrumbs />
        <page-title :text="$t('page_titles.favorites_page_title')" />
        <template v-if="!isLoading">
            <v-tabs v-model="selectedTabIndex" class="favorites-page__tabs" show-arrows center-active>
                <v-tab v-for="tab in tabs" :key="tab.name">
                    <v-icon class="me-1">
                        {{ tab.ico }}
                    </v-icon>
                    {{ $t(tab.title) }}
                </v-tab>
            </v-tabs>
            <v-row v-show="tabSelectedName === 'hotels' && hotels.length" class="hotels-tab mt-3">
                <v-col
                    v-for="(product, index) in hotels"
                    :key="`hotel-${index}`"
                    class="hotels-tab__item"
                    md="6"
                    lg="4"
                >
                    <component
                        :is="hotelComponent"
                        :offer="product"
                        :search-request="product.searchRequest"
                        hide-room-offers
                        hide-tripadvisor-rating
                        hide-show-on-map-button
                        class="hotel-card"
                    >
                        <template v-slot="{hotelPageLink}">
                            <div class="hotel-card__btn d-flex justify-end">
                                <v-btn v-if="hotelPageLink" :to="hotelPageLink" color="primary" variant="flat">
                                    {{ $t('book') }}
                                </v-btn>
                            </div>
                        </template>
                    </component>
                </v-col>
            </v-row>
            <v-row v-show="tabSelectedName === 'activities' && activities.length" class="activities-tab mt-1">
                <v-col v-for="(product, index) in activities" :key="`activity-${index}`" cols="12" md="6" lg="4">
                    <component
                        :is="activityComponent"
                        :product="product"
                        :search-request="product.searchRequest"
                        hide-price
                    >
                        <template v-slot>
                            <div class="activity-card__btn d-flex justify-end">
                                <v-btn :to="getPageLink(product)" color="primary" variant="flat">
                                    {{ $t('book') }}
                                </v-btn>
                            </div>
                        </template>
                    </component>
                </v-col>
            </v-row>
        </template>
        <v-row v-else>
            <v-col md="4"><v-skeleton-loader type="card" /></v-col>
        </v-row>
        <div
            v-if="
                !isLoading &&
                ((tabSelectedName === 'activities' && !activities.length) ||
                    (tabSelectedName === 'hotels' && !hotels.length))
            "
            class="pa-4"
        >
            {{ $t('favorite_list_is_empty') }}
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import HotelOfferCard from '~src/components/search/offers/hotelOfferCard.src'
    import HotelOfferCardMobile from '@/components/search/offers/HotelOfferCardMobile.vue'
    import ActivityCard from '@/components/activities/search/offers/ActivityCard.vue'
    import ActivityCardMobile from '@/components/activities/search/offers/ActivityCardMobile.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'
    import {AuthInfoMixin} from '~src/mixins/authInfoMixin.src'
    import {getCachedItems} from '@/utils/favorite'
    import {EventBus, LOAD_WISHLIST} from '@/utils/event-bus'

    @Component({
        components: {
            Breadcrumbs,
            HotelOfferCard,
            HotelOfferCardMobile,
            ActivityCard,
            ActivityCardMobile,
            PageTitle,
        },
    })
    export class FavoritesPage extends mixins(AuthInfoMixin) {
        selectedTabIndex = 0
        wishlist = []
        hotels = []
        activities = []
        counter = 0
        isLoading = true

        mounted() {
            EventBus.$on(LOAD_WISHLIST, this.load)
            this.load()
        }

        beforeUnmount() {
            EventBus.$off(LOAD_WISHLIST, this.load)
        }

        async load() {
            if (this.person) {
                try {
                    const userId = this.person.personId
                    const wishlist = await this.$api.wishlist.get({userId})
                    wishlist.forEach(item => (item.wishlist = true))
                    this.wishlist = wishlist

                    if (this.activeProducts.includes('hotels'))
                        this.hotels = this.wishlist.filter(item => item.productType === 'hotel')
                    if (this.activeProducts.includes('activities'))
                        this.activities = this.wishlist.filter(item => item.productType === 'activity')
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            } else {
                if (this.activeProducts.includes('hotels'))
                    this.hotels = await getCachedItems('hotelWishlist', hotelsStore.searchRequest)
                if (this.activeProducts.includes('activities'))
                    this.activities = await getCachedItems('activityWishlist', activitiesStore.searchRequest)
            }

            this.isLoading = false
        }

        getPageLink(product) {
            return activitiesRuntimeStore.infoPageLink(product, product.searchRequest)
        }

        get tabs() {
            const tabs = []
            if (this.activeProducts.includes('hotels')) {
                tabs.push({
                    ico: 'mdi-office-building',
                    title: 'hotels',
                    name: 'hotels',
                })
            }
            if (this.activeProducts.includes('activities')) {
                tabs.push({
                    ico: 'mdi-lightning-bolt-outline',
                    title: 'activities.activities',
                    name: 'activities',
                })
            }
            return tabs
        }

        get tabSelectedName() {
            return this.tabs[this.selectedTabIndex].name
        }

        get person() {
            return authStore.person
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get hotelComponent() {
            return !this.$breakpoint.smAndDown
                ? resolveComponent('HotelOfferCard')
                : resolveComponent('HotelOfferCardMobile')
        }

        get activityComponent() {
            return !this.$breakpoint.smAndDown
                ? resolveComponent('ActivityCard')
                : resolveComponent('ActivityCardMobile')
        }
    }

    export default FavoritesPage
</script>

<style lang="scss" scoped>
    :deep(.activity-card .price-col) {
        display: none;
    }
    :deep(.activity-card) {
        height: 100%;
    }
    :deep(.activity-card .wrapper) {
        height: unset;
        min-height: 200px;
    }
    :deep(.activity-card .short-description) {
        overflow-wrap: anywhere;
    }
</style>
